import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  DataTable,
  StatusChip,
  EmptyState,
  SkeletonLoader,
  type DataTableColumn,
} from '@shared/design-system/components';
import { listAdminSites, type AdminSite } from '../../api/adminClient';
import { lifecycleSeverity, presentationFor } from '../../lib/lifecycle';
import { DecommissionDialog } from './DecommissionDialog';
import { SiteDetail } from './SiteDetail';

/**
 * The configuration view, joined against the bus.
 *
 * This pane's job is not "list the sites" — the operator SPA and the
 * support console both do that. It is to surface DRIFT: the API owns
 * the SQL row while the operator owns the certificate, the ACL and the
 * worker container, nothing reconciles the two halves, and a lifecycle
 * sequence abandoned midway produces no error anywhere. It just
 * produces a site in a state no single system can see.
 *
 * So rows are ordered by severity, not alphabetically, and
 * `retired_still_publishing` sorts first unconditionally — it is the
 * one state that is a live security problem (a retired certificate
 * with bus write access) rather than an availability one.
 */
export function SitesPane() {
  const [selected, setSelected] = useState<string | null>(null);
  const [decommissioning, setDecommissioning] = useState<AdminSite | null>(null);
  const [onlyAttention, setOnlyAttention] = useState(false);

  const sitesQuery = useQuery({
    queryKey: ['admin', 'sites'],
    queryFn: () => listAdminSites(true),
    // Liveness moves; the configuration does not. 10s keeps
    // `lifecycle_state` honest without polling the DB pointlessly hard.
    refetchInterval: 10_000,
  });

  const sites = useMemo(() => {
    const all = sitesQuery.data ?? [];
    const filtered = onlyAttention
      ? all.filter((s) => presentationFor(s.lifecycle_state).needsAttention)
      : all;
    return [...filtered].sort(
      (a, b) =>
        lifecycleSeverity(a.lifecycle_state) - lifecycleSeverity(b.lifecycle_state) ||
        a.site_id.localeCompare(b.site_id),
    );
  }, [sitesQuery.data, onlyAttention]);

  const attentionCount = (sitesQuery.data ?? []).filter(
    (s) => presentationFor(s.lifecycle_state).needsAttention,
  ).length;

  if (sitesQuery.isLoading) {
    return <SkeletonLoader shape="table-row" count={5} />;
  }

  if (sitesQuery.isError) {
    return (
      <EmptyState
        title="Could not load sites"
        description={sitesQuery.error instanceof Error ? sitesQuery.error.message : 'Unknown error'}
      />
    );
  }

  const selectedSite = (sitesQuery.data ?? []).find((s) => s.site_id === selected) ?? null;

  const columns: DataTableColumn<AdminSite>[] = [
    {
      id: 'site',
      header: 'Site',
      cell: (row) => (
        <div>
          <div>{row.label}</div>
          <div style={{ fontSize: 12, color: 'var(--nx-muted)' }}>{row.site_id}</div>
        </div>
      ),
      sortable: true,
      sortValue: (row) => row.site_id,
    },
    {
      id: 'state',
      header: 'Lifecycle',
      cell: (row) => {
        const p = presentationFor(row.lifecycle_state);
        return (
          <span title={p.explanation}>
            <StatusChip status={p.status} label={p.label} />
          </span>
        );
      },
      sortable: true,
      sortValue: (row) => lifecycleSeverity(row.lifecycle_state),
    },
    {
      id: 'worker',
      header: 'Worker',
      cell: (row) => (
        <div style={{ fontSize: 12 }}>
          <div>{row.worker_status}</div>
          {row.worker_epoch !== null && (
            <div style={{ color: 'var(--nx-muted)' }}>epoch {row.worker_epoch}</div>
          )}
        </div>
      ),
    },
    {
      id: 'bus',
      header: 'Bus grant',
      cell: (row) =>
        row.eligible_for_bus_acl ? (
          // Deliberately not "granted": this API cannot read the
          // router's running ACL, only the DB predicate
          // `gen_bus_acl.py` derives its input from. Whether the ACL
          // has actually been regenerated since is unknowable here,
          // and claiming otherwise would be the exact false
          // reassurance this console exists to remove.
          <span title="This site is in gen_bus_acl.py's input set. Whether the ACL has been regenerated and the bus restarted since cannot be read from here.">
            <StatusChip status="active" label="eligible" />
          </span>
        ) : (
          <span title="Archived, or no platform_bus_endpoint — gen_bus_acl.py will not emit a grant for this site.">
            <StatusChip status="offline" label="not eligible" />
          </span>
        ),
    },
    {
      id: 'users',
      header: 'Users',
      cell: (row) => row.assigned_user_count,
      align: 'right',
      sortable: true,
      sortValue: (row) => row.assigned_user_count,
    },
    {
      id: 'version',
      header: 'Manifest',
      cell: (row) =>
        row.binding?.deployment_manifest_version ?? (
          <span style={{ color: 'var(--nx-muted)' }}>—</span>
        ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, flexWrap: 'wrap' }}>
        <h2 style={{ marginTop: 0, marginBottom: 8 }}>Sites &amp; deployment bindings</h2>
        <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            type="checkbox"
            checked={onlyAttention}
            onChange={(e) => setOnlyAttention(e.target.checked)}
            style={{ width: 'auto' }}
          />
          Needs attention only ({attentionCount})
        </label>
        <span style={{ fontSize: 12, color: 'var(--nx-muted)' }} aria-live="polite">
          {sitesQuery.isFetching ? 'Refreshing…' : 'Refreshes every 10s'}
        </span>
      </div>

      <p style={{ fontSize: 12, color: 'var(--nx-muted)', maxWidth: 900, marginTop: 0 }}>
        Lifecycle state joins this site’s database record against its live bus heartbeat. Anything
        other than <em>live</em> or <em>retired</em> means the two disagree.
      </p>

      <DataTable
        ariaLabel="Sites"
        columns={columns}
        rows={sites}
        getRowId={(row) => row.site_id}
        onRowClick={(row) => setSelected(row.site_id === selected ? null : row.site_id)}
        emptyState={
          <EmptyState
            title={onlyAttention ? 'Nothing needs attention' : 'No sites'}
            description={
              onlyAttention
                ? 'Every site’s record agrees with what the bus reports.'
                : 'No sites are registered yet. Use the Commission tab to add one.'
            }
          />
        }
      />

      {selectedSite && (
        <SiteDetail site={selectedSite} onDecommission={() => setDecommissioning(selectedSite)} />
      )}

      {decommissioning && (
        <DecommissionDialog site={decommissioning} onClose={() => setDecommissioning(null)} />
      )}
    </div>
  );
}
